// EB6 (register EB-CAN-06): the re-auth-available refusal vocabulary. A
// World-door refusal of one of these kinds is answered by ONE in-app door —
// the existing sign-in popup — never a terminal, never navigation to a page
// with a button. The kinds are the door's own closed vocabulary; the human
// reason is never a switch surface. A tool the World does not offer
// (grant_stale, tool_not_on_grant) is not among them: the World decides a
// member's tools live on every call, so signing in again changes nothing
// (Cleo Architecture Reset decision 2, 27 September 2026).
export const REAUTH_AVAILABLE_REFUSAL_KINDS = [
  "not_signed_in",
  "world_unresolved",
  "grant_rejected",
] as const;

export type ReauthAvailableRefusalKind = (typeof REAUTH_AVAILABLE_REFUSAL_KINDS)[number];

export function isReauthAvailableRefusal(kind: unknown): kind is ReauthAvailableRefusalKind {
  return (
    typeof kind === "string" && (REAUTH_AVAILABLE_REFUSAL_KINDS as readonly string[]).includes(kind)
  );
}

/**
 * W-4a6095af: what a person reads wherever the World stopped accepting this
 * Mac's sign-in (revoked or expired). One sentence for every surface; the
 * refusal kind (grant_rejected) stays in code and logs, never on screen.
 */
export const NEUCLEOS_SIGN_IN_ENDED_MESSAGE =
  "Your Neucleos sign-in on this Mac has ended. Sign in again to continue.";

/**
 * W-4a6095af follow-up: what a request says when the World answered 401 to it.
 * A 401 alone does not prove this Mac's sign-in ended: it may be meant for an
 * agent's own credential, a closed conversation lane or one engine's grant.
 * The Desk asks the World (grantRenewal.requestSignInCheck), and only the
 * World's "not live" signs the person out, with NEUCLEOS_SIGN_IN_ENDED_MESSAGE.
 */
export const NEUCLEOS_WORLD_DID_NOT_ACCEPT_MESSAGE =
  "Your World didn't accept this request from this Mac. Cleo is checking your sign-in and will ask you to sign in again if it has ended.";

/**
 * W-a02f7279, W-c0ac93da: what a person reads when a conversation could not
 * go on because this Mac's Neucleos sign-in ended (a grant expired, was
 * revoked, or was never deposited for this World). Each says what happened to
 * the message, what it cost, and the one thing to do. The server writes one as
 * the conversation's error; the Desk offers the one-press sign-in door beside
 * exactly these sentences and sends the message again once signed in.
 */
export const WORLD_SIGN_IN_ENDED_MESSAGES = {
  /** A Send stopped before Cleo started on it. */
  beforeSend:
    "Your Neucleos sign-in on this Mac has ended, so this message was not sent and nothing was charged for it. Sign in again to send it.",
  /** A Send stopped because Cleo could not ask the World how the last reply ended. */
  beforeConfirm:
    "Cleo could not check whether your last reply finished, because your Neucleos sign-in on this Mac has ended. This message was not sent and nothing was charged for it. Sign in again to send it.",
} as const;

/**
 * W-a02f7279: what a person reads when a Send waited on the World to confirm
 * how the conversation's last reply ended and it could not, for a reason other
 * than an ended sign-in. The machine reason stays in the log.
 */
export const PREVIOUS_REPLY_UNCONFIRMED_MESSAGE =
  "Your last reply may not have finished, and Cleo could not confirm it with your World yet. This message was not sent and nothing was charged for it; anything the last reply was charged shows in Usage. Send it again in a moment.";

/**
 * The re-auth door a conversation error calls for, or null. Only the sentences
 * above qualify; any other error names its own next step.
 */
export function reauthKindForConversationError(
  error: string | null | undefined,
): ReauthAvailableRefusalKind | null {
  if (typeof error !== "string") return null;
  return Object.values(WORLD_SIGN_IN_ENDED_MESSAGES).some((sentence) => error.includes(sentence))
    ? "grant_rejected"
    : null;
}
